import type {
  ApiDashboardElement,
  ApiImageGallery,
  ApiReturnMeasuresValidated,
} from '@highimpact-frontend/api-types';
import { createAsyncThunk } from '@reduxjs/toolkit';

import type { DashboardLibraryThunkExtra } from '../api/httpTransport';
import { hydrateDashboardLibraryState } from './actions';
import { setDashboardElement, setImageGalleryTags } from './dashboard/slice';
import {
  getDashboardAtKey,
  getDashboardSlice,
  getWidgetAtKey,
  removeDashboardSafely,
  removeWidgetSafely,
} from './store-operations';
import type { RootState } from './types';

interface deleteWidgetByElementIdProps {
  dashboardElementId: number;
  gridType: 'grids' | 'reports';
}

export const deleteWidgetByElementId = createAsyncThunk<
  void,
  deleteWidgetByElementIdProps,
  { extra: DashboardLibraryThunkExtra }
>(
  'data/delete-widget',
  async (
    { dashboardElementId: elementId, gridType },
    thunkAPI
  ): Promise<void> => {
    const state = thunkAPI.getState() as RootState;
    if (state && elementId) {
      await thunkAPI.extra.api.dashboard.deleteDashboardWidget(elementId);
      const newState = removeWidgetSafely(elementId, gridType, state);
      thunkAPI.dispatch(hydrateDashboardLibraryState(newState));
    }
    return Promise.resolve();
  }
);

export const deleteDashboardById = createAsyncThunk<
  void,
  number,
  { extra: DashboardLibraryThunkExtra }
>(
  'data/delete-dashboard',
  async (dashboardId, thunkAPI): Promise<void> => {
    const state = thunkAPI.getState() as RootState;
    if (state && dashboardId) {
      await thunkAPI.extra.api.dashboard.deleteDashboard(dashboardId);
      const newState = removeDashboardSafely(dashboardId, 'grids', state);
      thunkAPI.dispatch(hydrateDashboardLibraryState(newState));
    }
    return Promise.resolve();
  }
);

export const updateAndPersistImageGalleryTags = createAsyncThunk<
  void,
  {
    gridType: 'grids' | 'reports';
    gridId: number;
    widgetId: string;
    tags: string[];
  },
  { extra: DashboardLibraryThunkExtra }
>(
  'dashboard/update-and-persist-image-gallery-tags',
  async (params, thunkAPI) => {
    const { gridId, gridType, widgetId, tags } = params;
    const state = thunkAPI.getState() as RootState;
    const dashboardSlice = getDashboardSlice(state);
    const dashboard = getDashboardAtKey(gridId, gridType, dashboardSlice);
    if (!dashboard) {
      throw new Error(`Dashboard with id ${gridId} not found. Cannot proceed.`);
    }
    const widget = getWidgetAtKey(widgetId, dashboard);
    if (!widget) {
      throw new Error(`Widget with id ${widgetId} not found. Cannot proceed.`);
    }
    const dashboardElement = { ...widget.elementData } as ApiImageGallery;
    dashboardElement.properties = { ...dashboardElement.properties, tags };
    await thunkAPI.extra.api.dashboard.putDashboardElement(
      dashboardElement.id,
      dashboardElement
    );
    thunkAPI.dispatch(
      setImageGalleryTags({ gridId, gridType, widgetId, tags })
    );
  }
);

export const updateAndPersistDashboardElement = createAsyncThunk<
  void,
  {
    gridType: 'grids' | 'reports';
    gridId: number;
    widgetId: string;
    dashboardElement: ApiDashboardElement;
  },
  { extra: DashboardLibraryThunkExtra }
>(
  'dashboard/update-and-persist-dashboard-element',
  async (params, thunkAPI) => {
    const { gridId, gridType, widgetId, dashboardElement } = params;
    const state = thunkAPI.getState() as RootState;
    const dashboardSlice = getDashboardSlice(state);
    const dashboard = getDashboardAtKey(gridId, gridType, dashboardSlice);
    if (!dashboard) {
      throw new Error(`Dashboard with id ${gridId} not found. Cannot proceed.`);
    }
    const widget = getWidgetAtKey(widgetId, dashboard);
    if (!widget) {
      throw new Error(`Widget with id ${widgetId} not found. Cannot proceed.`);
    }
    const dashboardElementId = widget.dashboardElementId ?? dashboardElement.id;
    if (!dashboardElementId) {
      throw new Error(
        `Dashboard element id for widget with id ${widgetId} not found. Cannot proceed.`
      );
    }
    await thunkAPI.extra.api.dashboard.putDashboardElement(
      dashboardElementId,
      dashboardElement
    );
    thunkAPI.dispatch(
      setDashboardElement({ gridId, gridType, widgetId, dashboardElement })
    );
  }
);

export const fetchDataWithParams = createAsyncThunk<
  { url: string; data: ApiReturnMeasuresValidated },
  string,
  { extra: DashboardLibraryThunkExtra; rejectValue: string }
>('dataWithParams/fetch', async (url, thunkAPI) => {
  try {
    const res = await thunkAPI.extra.transport.dataDashboard.get(url);
    if (res.status >= 400) throw new Error('Network error');
    const data = (await res.data) as ApiReturnMeasuresValidated;
    return { url, data };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    return thunkAPI.rejectWithValue(message);
  }
});
